import React, { useState } from 'react'
import { BODY_FONT, HEAD_FONT } from '../lib/palette'
import { primaryBtn, secondaryBtn } from '../lib/styles'
import { REQUIREMENTS } from '../../api/passwordPolicy'

// The policy module is CommonJS and lives under src/api, which webpack resolves happily — both
// trees are inside src/. Importing it rather than restating the rules means the list shown here
// cannot drift from the list the server enforces.

const AuthScreen = ({ palette, mode, onModeChange, onSubmit, error, busy }) => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [inviteCode, setInviteCode] = useState('')
    const [revealed, setRevealed] = useState(false)

    const isRegister = mode === 'register'

    const styles = {
        root: {
            minHeight: '100vh',
            background: palette.bg,
            fontFamily: BODY_FONT,
            color: palette.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            boxSizing: 'border-box',
        },
        card: { width: '100%', maxWidth: '380px' },
        title: { fontFamily: HEAD_FONT, fontWeight: 600, fontSize: '30px', margin: '0 0 6px' },
        tagline: { margin: '0 0 28px', color: palette.sub, fontSize: '15px', lineHeight: 1.5 },
        group: { marginBottom: '16px' },
        label: {
            display: 'block',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: palette.sub,
            marginBottom: '8px',
        },
        input: {
            width: '100%',
            fontFamily: BODY_FONT,
            fontSize: '14px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: `1px solid ${palette.line}`,
            boxSizing: 'border-box',
            background: 'transparent',
            color: palette.ink,
        },
        reveal: {
            fontFamily: BODY_FONT,
            fontSize: '12px',
            background: 'none',
            border: 'none',
            color: palette.sub,
            cursor: 'pointer',
            padding: '6px 0 0',
            textDecoration: 'underline',
        },
        requirements: { margin: '8px 0 0', paddingLeft: '18px', color: palette.sub, fontSize: '12px', lineHeight: 1.6 },
        errorText: {
            fontSize: '13px',
            fontWeight: 500,
            color: `oklch(45% 0.15 ${palette.negHue})`,
            margin: '0 0 12px',
        },
        actions: { display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap', alignItems: 'center' },
    }

    const submit = event => {
        event.preventDefault()
        if (busy) return
        onSubmit(isRegister ? { email, password, inviteCode } : { email, password })
    }

    return (
        <div style={styles.root}>
            <div style={styles.card}>
                <h1 style={styles.title}>Now Map</h1>
                <p style={styles.tagline}>
                    {isRegister
                        ? 'Create an account to keep your own private map.'
                        : 'Sign in to see your reflections.'}
                </p>
                <form onSubmit={submit}>
                    <div style={styles.group}>
                        <label htmlFor="auth-email" style={styles.label}>
                            Email
                        </label>
                        <input
                            id="auth-email"
                            style={styles.input}
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                    </div>
                    <div style={styles.group}>
                        <label htmlFor="auth-password" style={styles.label}>
                            Password
                        </label>
                        <input
                            id="auth-password"
                            style={styles.input}
                            // Not type="password" when revealed: a 14-character password with
                            // four character classes is one people paste or check as they type.
                            type={revealed ? 'text' : 'password'}
                            autoComplete={isRegister ? 'new-password' : 'current-password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                        />
                        <button type="button" style={styles.reveal} onClick={() => setRevealed(r => !r)}>
                            {revealed ? 'Hide password' : 'Show password'}
                        </button>
                        {/* Shown up front, not after a rejection: rules discovered by failing are
                            rules people resent. */}
                        {isRegister && (
                            <ul style={styles.requirements}>
                                {REQUIREMENTS.map(requirement => (
                                    <li key={requirement}>{requirement}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {isRegister && (
                        <div style={styles.group}>
                            <label htmlFor="auth-invite" style={styles.label}>
                                Invite code
                            </label>
                            <input
                                id="auth-invite"
                                style={styles.input}
                                value={inviteCode}
                                onChange={e => setInviteCode(e.target.value)}
                            />
                        </div>
                    )}
                    {error && (
                        <p style={styles.errorText} role="alert">
                            {error}
                        </p>
                    )}
                    <div style={styles.actions}>
                        <button type="submit" style={primaryBtn(palette)} disabled={busy}>
                            {isRegister ? 'Create account' : 'Sign in'}
                        </button>
                        <button
                            type="button"
                            style={secondaryBtn(palette)}
                            onClick={() => onModeChange(isRegister ? 'signin' : 'register')}
                        >
                            {isRegister ? 'I already have an account' : 'Create an account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AuthScreen
